import { GQL_ID, ProductFilter } from '@@types'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { ReactSVG } from 'react-svg'
import { useMediaQuery } from 'usehooks-ts'


import { useProductFilters } from '@entities/Product/api/productFilters.gql'
import { useProductFiltersStore } from '@entities/Product/model/useFiltersStore'

import { useModalStore } from '@features/modal'
import { useAddressStore } from '@features/shipping-methods'


import { QUERY_NAMES } from '@shared/contants'
import { useDeferredFilters, useFilters } from '@shared/hooks'
import { cn } from '@shared/lib/cn'
import { DynamicFilters } from '@shared/types'
import Button from '@shared/ui/button'
import FilterContains, {
  FilterContainsProps,
} from '@shared/ui/filter-contains'
import RangeDecimal from '@shared/ui/range-demical'

import styles from './styles.module.scss'


interface ProductFiltersProps {
  hideTitle?: boolean;
  isModalStyle?: boolean;
  afterSubmitCallback?: () => void;
  afterResetCallback?: () => void;
  externalFilters?: ProductFilter[];
  categoryId?: GQL_ID;
}

function ProductFiltersLoading () {
  return (
    <SkeletonTheme>
      <Skeleton
        count={10}
        width={'100%'}
        height={30}
        style={{
          borderRadius: 6,
          marginBottom: 20,
        }}
      />
    </SkeletonTheme>
  )
}

export default function ProductFilters (props: ProductFiltersProps) {
  const { closeModal } = useModalStore()
  const isTablet = useMediaQuery('(max-width: 991px)')
  const { t: tc } = useTranslation('common')
  const { t: ts } = useTranslation('search')

  const {
    hideTitle,
    isModalStyle,
    afterResetCallback,
    afterSubmitCallback,
    externalFilters,
    categoryId
  } = props
  const { isValid, setIsValid } = useProductFiltersStore()
  const { shopId } = useAddressStore()
  const shouldUseQuery = !externalFilters

  const { filters: fetchedFilters, loading } = useProductFilters({
    shop_id: shopId,
    category_id: categoryId,
  })

  const productFilters = externalFilters ?? (shouldUseQuery ? fetchedFilters : [])

  const {
    filters: urlFilters,
    changeFilters,
    resetFilters,
  } = useFilters<DynamicFilters>({
    groupKey: QUERY_NAMES.PRODUCT_FILTERS,
  })

  const { localFilters, setLocalFilters } = useDeferredFilters(urlFilters)

  useEffect(() => {
    setLocalFilters(urlFilters)
    setIsValid(true)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ urlFilters ])

  if (shouldUseQuery && loading) return <ProductFiltersLoading />
  if (!productFilters.length) return null

  return (
    <div
      className={cn(styles.root, {
        [styles['is-modal']]: isModalStyle,
      })}
    >
      {!hideTitle && (
        <h3 className={styles.root__title}>{tc('filters.title')}</h3>
      )}

      <div className={styles.root__body}>
        {productFilters.map((filter) => {
          if (
            filter?.type === 'decimal' &&
            filter?.op === 'between' &&
            Array.isArray(filter?.values)
          ) {
            const min = parseFloat(
              filter.values.find((v) => v && v.value === 'min')?.name || '0',
            )
            const max = parseFloat(
              filter.values.find((v) => v && v.value === 'max')?.name || '0',
            )

            const fromKey = `${filter.filterInputFieldName}_from`
            const toKey = `${filter.filterInputFieldName}_to`

            const valueFrom = localFilters[fromKey]
            const valueTo = localFilters[toKey]

            return (
              <RangeDecimal
                key={filter.id}
                value={[ valueFrom as number, valueTo as number ]}
                onChange={(val) => {
                  setLocalFilters((prev) => ({
                    ...prev,
                    [fromKey]: val[0],
                    [toKey]: val[1],
                  }))
                }}
                min={min}
                max={max}
                defaultOpen
                name={filter.label}
                showCurrency={filter.id === 'price'}
              />
            )
          }

          if (filter?.type === 'list') {
            const filterKey = filter.filterInputFieldName as string
            const selectedValues = Array.isArray(localFilters[filterKey])
              ? (localFilters[filterKey] as string[])
              : []

            return (
              <FilterContains
                withSearch={
                  filter.id === 'brand' || filter.id === 'manufacturer'
                }
                key={filter.id}
                name={filter?.label as string}
                values={
                  filter.values
                    ?.filter((v) => v?.name?.trim())
                    .map((v) => ({
                      name: v?.name,
                      value: v?.value,
                    })) as FilterContainsProps['values']
                }
                selected={selectedValues}
                onChange={(value, checked) => {
                  setLocalFilters((prev) => {
                    const current = (prev[filterKey] ?? []) as string[]
                    let updated

                    if (checked) {
                      updated = [ ...new Set([ ...current, value ]) ]
                    }
                    else {
                      updated = current.filter((v) => v !== value)
                    }

                    return {
                      ...prev,
                      [filterKey]: updated.length > 0 ? updated : undefined,
                    }
                  })
                }}
              />
            )
          }

          return null
        })}
      </div>

      {!isValid && (
        <span className={styles.root__message}>
          <ReactSVG
            className={styles['root__message-icon']}
            src="/icons/info-circle.svg"
          />
          {ts('empty.description2')}
        </span>
      )}

      <div className={styles.root__bottom}>
        {isValid && (
          <Button
            className={styles.root__save}
            onClick={() => {
              changeFilters(localFilters)
              if (afterSubmitCallback) afterSubmitCallback()
              closeModal('productFilters')
            }}
            size={isTablet ? 'medium' : 'large'}
          >
            {ts('filter.applyBtn')}
          </Button>
        )}

        <Button
          className={styles.root__save}
          onClick={() => {
            setLocalFilters({})
            resetFilters()
            if (afterResetCallback) afterResetCallback()
            closeModal('productFilters')
          }}
          size={isTablet ? 'medium' : 'large'}
          theme="grey"
        >
          {ts('filter.resetBtn')}
        </Button>
      </div>
    </div>
  )
}
